/****************************************************************************
 **
 ** Copyright (C) 2021 The Qt Company Ltd.
 ** Contact: https://www.qt.io/licensing/
 **
 ** This file is part of Qt Bridge Plugin.
 **
 ** Commercial License Usage
 ** Licensees holding valid commercial Qt licenses may use this file in
 ** accordance with the commercial license agreement provided with the
 ** Software or, alternatively, in accordance with the terms contained in
 ** a written agreement between you and The Qt Company. For licensing terms
 ** and conditions see https://www.qt.io/terms-conditions. For further
 ** information use the contact form at https://www.qt.io/contact-us.
 **
 ****************************************************************************/

import Document from "sketch/dom";
import BrowserWindow from "sketch-module-web-view";
import UI from "sketch/ui";

import { ValidExportFormats as VEF } from "./constants.js";
import * as Constants from "./constants.js";
import { Export } from "./export.js";
import { ViewApi, BUtils } from "./qtbridgeutils.js";
import { SLayer } from "./slayer.js";
import { SDocument } from "./sdocument.js";

export default function (context) {
    if (BUtils.isPanelOpen()) {
        return;
    }
    BUtils.init();
    const options = {
        identifier: "qtbridge.window.panel",
        width: Constants.PanelWidth,
        height: Constants.PanelHeight,
        show: false,
        resizable: false,
        minimizable: false,
        maximizable: false,
        alwaysOnTop: true
    };
    const panelWindow = new BrowserWindow(options);
    panelWindow.loadURL(require("../resources/index.html"));
    panelWindow.once("ready-to-show", () => {
        ViewApi("updateTheme", UI.getTheme());
        pushSelectedLayersDetails();
        pushVersionDetails();
        panelWindow.show();
    });
    registerWebviewApis(panelWindow);
}

function registerWebviewApis(panelWindow) {
    panelWindow.webContents.on("exportActiveDocument", apiExportActiveDocument);
    panelWindow.webContents.on("setLayerMetadata", apiSetLayerMetadata);
    panelWindow.webContents.on("setDocumentMetadata", apiSetDocumentMetadata);
    panelWindow.webContents.on("resetDocumentMetadata", apiResetDocumentMetadata);
    panelWindow.webContents.on("resetLayerMetadata", apiResetLayerMetadata);
    panelWindow.webContents.on("setUserExportPath", apiSetUserExportPath);
    panelWindow.webContents.on("showExportDir", apiShowExportDir);
    panelWindow.webContents.on("updateDocumentDetails", apiUpdateDocumentDetails);
    panelWindow.webContents.on("showLicense", apiShowLicense);
    panelWindow.webContents.on("showHelp", apiShowHelp);
    panelWindow.webContents.on("setFormatOptions", apiSetFormatOptions);
    panelWindow.webContents.on("setOrganizeInPages", apiSetOrganizeInPages);
    panelWindow.webContents.on("setUiScale", apiSetUiScale);

}

//*****************************************************************************
// Apis exposed to Web view.
//*****************************************************************************
function apiExportActiveDocument() {
    Export.exportActiveDocument();
}

function apiShowLicense() {
    let url = BUtils.resourcePath("license.txt");
    NSWorkspace.sharedWorkspace().openFile_withApplication(url, "TextEdit");
}

function apiShowExportDir() {
    let sDoc = new SDocument(Document.getSelectedDocument());
    let url = NSURL.fileURLWithPath(sDoc.exportPath());
    NSWorkspace.sharedWorkspace().openURL(url);
}

function apiShowHelp() {
    let url = NSURL.URLWithString(BUtils.helpLink());
    NSWorkspace.sharedWorkspace().openURL(url);
}

function apiSetLayerMetadata(params) {
    let data = JSON.parse(params);
    const currentDocument = Document.getSelectedDocument();
    currentDocument.selectedLayers.forEach(instance => {
        let sLayer = new SLayer(instance);
        if (!sLayer.setMetadata(data.name, data.value)) {
            BUtils.logError(`Layer set metadata failed. Name: {data.name} Value: {data.value}`);
        }
    });
    pushSelectedLayersDetails();
}

function apiSetDocumentMetadata(params) {
    let data = params;
    if (params.constructor == String) {
        data = JSON.parse(params);
    }
    const currentDocument = Document.getSelectedDocument();
    if (currentDocument) {
        let sDoc = new SDocument(currentDocument);
        sDoc.setMetadata(data.name, data.value);
    }
}

function apiResetLayerMetadata(name) {
    let propertyName = JSON.parse(name);
    const currentDocument = Document.getSelectedDocument();
    currentDocument.selectedLayers.forEach(instance => {
        let sLayer = new SLayer(instance);
        if (!sLayer.resetMetadata(propertyName)) {
            BUtils.logError(`Layer ${sLayer.name()} reset metadata failed for ${propertyName}`);
        }
    });
    pushSelectedLayersDetails();
}

function apiResetDocumentMetadata(name) {
    let propertyName = JSON.parse(name);
    const currentDocument = Document.getSelectedDocument();
    if (currentDocument) {
        let sDoc = new SDocument(currentDocument);
        sDoc.resetMetadata(propertyName);
        pushDocumentDetails();
    }
}

function apiSetUserExportPath() {
    let path = BUtils.getDirPath();
    if (path) {
        apiSetDocumentMetadata({ name: "jsonExportPath", value: path });
        pushDocumentDetails();
    }
}

function apiUpdateDocumentDetails() {
    // See QDS-545
    pushDocumentDetails();
}

function apiSetFormatOptions(options) {
    const currentDocument = Document.getSelectedDocument();
    if (currentDocument) {
        let sDoc = new SDocument(currentDocument);
        sDoc.setFormatOptions(JSON.parse(options));
    }
}

function apiSetOrganizeInPages(value) {
    const currentDocument = Document.getSelectedDocument();
    if (currentDocument) {
        let sDoc = new SDocument(currentDocument);
        sDoc.setOrganizeInPages(JSON.parse(value));
    }
}

function apiSetUiScale(value) {
    const currentDocument = Document.getSelectedDocument();
    if (currentDocument) {
        let sDoc = new SDocument(currentDocument);
        sDoc.setUiScaleFactor(JSON.parse(value));
    }
    pushDocumentDetails();
}

//*****************************************************************************
// Private Api's
//*****************************************************************************
export function pushDocumentDetails() {
    const currentDocument = Document.getSelectedDocument();
    if (currentDocument) {
        let sDoc = new SDocument(currentDocument);
        ViewApi("onDocDetailsChanged", sDoc.normalizedDocumentInfo());
    }
}

export function pushSelectedLayersDetails() {
    const document = Document.getSelectedDocument();
    let sDoc = new SDocument(document);
    let selectionData = [];
    document.selectedLayers.forEach(item => {
        let sLayer = new SLayer(item);
        let data = sLayer.normalizedLayerInfo();
        data.documentName = sDoc.documentName(); // See QDS-545
        selectionData.push(data);
    });
    ViewApi("onLayerSelectionChange", selectionData);
}

export function pushVersionDetails() {
    ViewApi("onVersionDetails", BUtils.version(), BUtils.build());
}
